import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiAlertCircle, FiInfo, FiAlertTriangle, FiX } from 'react-icons/fi';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    // Fallback if used outside provider
    return {
      success: (msg) => console.log('SUCCESS:', msg),
      error: (msg) => console.error('ERROR:', msg),
      warning: (msg) => console.warn('WARNING:', msg),
      info: (msg) => console.info('INFO:', msg),
    };
  }
  return context;
};

const toastConfig = {
  success: {
    icon: FiCheckCircle,
    bg: 'bg-gradient-to-r from-teal-500 to-emerald-500',
    iconBg: 'bg-white/20',
    accent: 'bg-teal-400',
  },
  error: {
    icon: FiAlertCircle,
    bg: 'bg-gradient-to-r from-red-500 to-rose-500',
    iconBg: 'bg-white/20',
    accent: 'bg-red-400',
  },
  warning: {
    icon: FiAlertTriangle,
    bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
    iconBg: 'bg-white/20',
    accent: 'bg-amber-400',
  },
  info: {
    icon: FiInfo,
    bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
    iconBg: 'bg-white/20',
    accent: 'bg-blue-400',
  },
};

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((type, message, duration = 4000) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, type, message }]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = {
    success: (msg) => addToast('success', msg),
    error: (msg) => addToast('error', msg, 5000),
    warning: (msg) => addToast('warning', msg),
    info: (msg) => addToast('info', msg),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none" style={{ maxWidth: '420px', width: '100%' }}>
        <AnimatePresence mode="popLayout">
          {toasts.map((t) => {
            const config = toastConfig[t.type] || toastConfig.info;
            const Icon = config.icon;
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, x: 80, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 80, scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className={`${config.bg} rounded-2xl shadow-2xl overflow-hidden pointer-events-auto`}
                style={{ boxShadow: '0 20px 60px -15px rgba(0,0,0,0.3)' }}
              >
                {/* Progress bar */}
                <motion.div
                  className={`h-1 ${config.accent}`}
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: t.type === 'error' ? 5 : 4, ease: 'linear' }}
                />
                <div className="flex items-start gap-3 px-5 py-4">
                  <div className={`flex-shrink-0 w-10 h-10 ${config.iconBg} rounded-xl flex items-center justify-center backdrop-blur-sm`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-sm font-semibold text-white/90 capitalize">{t.type}</p>
                    <p className="text-sm text-white/75 mt-0.5 leading-relaxed">{t.message}</p>
                  </div>
                  <button
                    onClick={() => removeToast(t.id)}
                    className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors mt-0.5"
                  >
                    <FiX className="w-4 h-4 text-white/70" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
